import { moduleDefectApi } from '@/apis/module.defect.api';
import { extractWgs84Point } from '@/utils/defect/extractWgs84Point';
import {
  invalidateModuleDefectDetailCache,
  setModuleDefectDetailCache
} from '@/utils/moduleDefectDetailCache';
import {
  logDefectModuleLink,
  summarizeDefectModuleLinkState
} from '@/utils/debug/defectModuleLinkDebug';

/** 드래그 종료 시 부동소수점 노이즈만 무시 (~sub-mm). 실제 미세 이동은 모두 API 대상 */
const COORD_UNCHANGED_EPS = 1e-12;

/**
 * 시작 좌표 대비 변경 여부. 1m 임계값 없이 미세 조정도 변경으로 본다.
 * @param {number | null | undefined} startLon
 * @param {number | null | undefined} startLat
 * @param {number} lon
 * @param {number} lat
 */
export function hasMeaningfulCoordsMove(startLon, startLat, lon, lat) {
  if (startLon == null || startLat == null) {
    return true;
  }
  return (
    Math.abs(lon - startLon) > COORD_UNCHANGED_EPS || Math.abs(lat - startLat) > COORD_UNCHANGED_EPS
  );
}

/**
 * coords PATCH 직후 GET은 이전 좌표를 줄 수 있다 — PATCH로 보낸 경위도를 우선한다.
 * @param {unknown} serverDetail
 * @param {number} lon
 * @param {number} lat
 */
export function resolveCoordsAfterAdjustPatch(serverDetail, lon, lat) {
  const serverPoint = extractWgs84Point(serverDetail?.defectLocation);
  if (serverPoint != null && !hasMeaningfulCoordsMove(serverPoint.lon, serverPoint.lat, lon, lat)) {
    return serverPoint;
  }
  return { lon, lat };
}

function withPatchedDefectLocation(defectLocation, lon, lat) {
  if (defectLocation == null) {
    return { type: 'Point', coordinates: [lon, lat] };
  }
  if (Array.isArray(defectLocation)) {
    return [lon, lat];
  }
  if (typeof defectLocation !== 'object') {
    return { type: 'Point', coordinates: [lon, lat] };
  }
  if (Array.isArray(defectLocation.coordinates)) {
    return { ...defectLocation, coordinates: [lon, lat] };
  }
  if (defectLocation.position && typeof defectLocation.position === 'object') {
    return {
      ...defectLocation,
      position: {
        ...defectLocation.position,
        coordinate: [lon, lat],
        coordinates: [lon, lat]
      }
    };
  }
  return { type: 'Point', coordinates: [lon, lat] };
}

/**
 * coords PATCH 성공 후 결함 1건만 store·캐시·지도 피처 속성을 갱신한다 (WFS 전체 reload 없음).
 *
 * PATCH 자체로 서버의 solarModule 연결은 완료되므로, GET 이 실패(403 등)해도
 * 지도 좌표·store 는 갱신한다. 모듈 정보는 feature 기존 속성에서 유지하고
 * WFS 재조회 시 최신 모듈로 교체된다.
 *
 * @param {import('vuex').Store} store
 * @param {number | string} defectId
 * @param {number} lon
 * @param {number} lat
 * @param {import('ol/Feature').default | null | undefined} [feature]
 */
export async function syncDefectCoordsAdjustMinimal(store, defectId, lon, lat, feature) {
  invalidateModuleDefectDetailCache(defectId);

  const featureProps = feature?.getProperties?.() || {};

  logDefectModuleLink('sync-minimal-start', {
    defectId,
    patchLon: lon,
    patchLat: lat,
    featureSolarModuleSeq: featureProps.solar_module_seq ?? featureProps.solarModuleSeq ?? null
  });

  let detail = null;
  try {
    detail = await moduleDefectApi.getDefectById(defectId);
    logDefectModuleLink('sync-minimal-get-after-patch', {
      defectId,
      ...summarizeDefectModuleLinkState(detail)
    });
  } catch (error) {
    logDefectModuleLink('sync-minimal-get-after-patch-failed', {
      defectId,
      message: error?.message ?? String(error)
    });
  }

  const point = resolveCoordsAfterAdjustPatch(detail, lon, lat);
  const defectLocation = withPatchedDefectLocation(detail?.defectLocation, point.lon, point.lat);

  // GET 성공 시 서버의 solarModule 사용, 실패 시 WFS reload 로 대체하므로 solarModuleSeq 갱신 안 함
  // Spring Data REST HAL: solarModule 은 _embedded.solarModule 에 있음
  const solarModule = detail?.solarModule ?? detail?._embedded?.solarModule ?? null;
  const solarModuleSeq = solarModule?.id ?? solarModule?.seq ?? null;
  const moduleId = solarModule?.moduleId ?? null;

  // GET 성공 여부와 관계없이 좌표는 PATCH 로 보낸 값으로 갱신
  // solarModuleSeq 는 GET 성공 시에만 갱신, 실패 시 WFS reload 에서 최신값 반영
  const patchBase = {
    lng: point.lon,
    lat: point.lat,
    /** 마커를 움직였으므로 지도 위치 검증 완료 */
    mapPositionVerified: true
  };
  if (detail) {
    patchBase.solarModuleSeq = solarModuleSeq;
  }

  store.commit('dashboard/patchWfsDefectRow', {
    seq: defectId,
    patch: patchBase
  });

  // solarModuleSeq 가 null 일 때도 patchThermalImageModuleDefect 는 호출 (좌표만 갱신)
  store.commit('dashboard/patchThermalImageModuleDefect', {
    defectId,
    patch: {
      defectLocation,
      solarModule,
      ...(detail ? { solarModuleSeq } : {})
    }
  });

  const mergedDetail = detail
    ? { ...detail, defectLocation, solarModule }
    : { id: defectId, defectLocation, solarModule: null };

  setModuleDefectDetailCache(defectId, mergedDetail);

  if (feature?.getGeometry?.()) {
    feature.getGeometry().setCoordinates([point.lon, point.lat]);
  }

  // GET 성공 시에만 feature 속성의 solarModuleSeq 갱신 (실패 시 WFS reload 에서 교체)
  if (feature?.setProperties && detail) {
    feature.setProperties({
      ...featureProps,
      solar_module_seq: solarModuleSeq,
      solarModuleSeq: solarModuleSeq,
      module_id: moduleId ?? featureProps.module_id
    });
  }

  logDefectModuleLink('sync-minimal-store-patched', {
    defectId,
    storeWfsPatch: patchBase,
    thermalImagePatch: {
      solarModuleSeq: detail ? solarModuleSeq : '(GET failed — unchanged)',
      moduleId: detail ? moduleId : null
    }
  });

  return mergedDetail;
}
